import { Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocalDate } from "@js-joda/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import { addWorkDays, EditMortgageTimelineBodySchema } from "shared";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";
import { useMortgage } from "@/mortgages/hooks";

interface EditMortgageTimelineProps {
  mortgageID: number;
}

export const EditMortgageTimeline = (props: EditMortgageTimelineProps) => {
  const dialog = useDialog();
  const mortgage = useMortgage(props.mortgageID);

  const { handleSubmit, formState, control, watch, setValue } = useForm({
    resolver: zodResolver(EditMortgageTimelineBodySchema),
    defaultValues: {
      lastPaymentDate: mortgage.data.payoff.lastPaymentDate.toString(),
      estClosingDate: mortgage.data.payoff.estClosingDate.toString(),
      fundingDate: mortgage.data.payoff.fundingDate.toString(),
    },
  });
  const queryClient = useQueryClient();

  const estClosingDate = watch("estClosingDate");

  useEffect(() => {
    if (!mortgage.data.payoff.fundingDateModified) {
      const date = LocalDate.parse(estClosingDate);
      setValue("fundingDate", addWorkDays(date, 4).toString());
    }
  }, [estClosingDate, setValue, mortgage.data.payoff.fundingDateModified]);

  const onSubmit = handleSubmit(async (values) => {
    await Api.MortgageUpdateTimeline(props.mortgageID, values).then(
      (res) => res.data,
    );
    await queryClient.refetchQueries({
      queryKey: QueryKeys.Mortgage(props.mortgageID),
    });
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Edit mortgage timeline</Dialog.Header>
      <Dialog.Body>
        <TextField
          control={control}
          name="lastPaymentDate"
          label="Last payment date"
          type="date"
          required
        />
        <TextField
          control={control}
          name="estClosingDate"
          label="Estimated closing date"
          type="date"
          required
        />
        <TextField
          control={control}
          name="fundingDate"
          label="Funding date"
          type="date"
          required
        />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
